import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContextNew';
import DashboardLayout from './modules/DashboardLayout';

// Lazy-loaded pages
const LoginPage = lazy(() => import('./modules/LoginPage'));
const DashboardPage = lazy(() => import('./modules/dashboard/DashboardPage'));
const TrainingPage = lazy(() => import('./modules/compliance/TrainingPage'));
const InterventionsPage = lazy(() => import('./modules/compliance/InterventionsPage'));
const ReportsPage = lazy(() => import('./modules/compliance/ReportsPage'));
const SELPlannerPage = lazy(() => import('./modules/classroom/SELPlannerPage'));
const HolisticEvaluationPage = lazy(() => import('./modules/classroom/HolisticEvaluationPage'));
const ReferralsPage = lazy(() => import('./modules/classroom/ReferralsPage'));
const EventsPage = lazy(() => import('./modules/parents/EventsPage'));
const CounsellingPage = lazy(() => import('./modules/parents/CounsellingPage'));
const MoodCheckInPage = lazy(() => import('./modules/students/MoodCheckInPage'));
const CareerExplorerPage = lazy(() => import('./modules/students/CareerExplorerPage'));
const MyCounsellingPage = lazy(() => import('./modules/students/MyCounsellingPage'));
const ChildProgressPage = lazy(() => import('./modules/parents/ChildProgressPage'));
const AnalyticsPage = lazy(() => import('./modules/analytics/AnalyticsPage'));
const UsersPage = lazy(() => import('./modules/management/UsersPage'));
const SettingsPage = lazy(() => import('./modules/management/SettingsPage'));
const AuditLogPage = lazy(() => import('./modules/management/AuditLogPage'));
const NotificationsManagePage = lazy(() => import('./modules/management/NotificationsManagePage'));
const DailyJournalPage = lazy(() => import('./modules/students/DailyJournalPage'));
const AnnouncementsPage = lazy(() => import('./modules/management/AnnouncementsPage'));
const AttendancePage = lazy(() => import('./modules/management/AttendancePage'));
const FeedbackPage = lazy(() => import('./modules/management/FeedbackPage'));
const TimetablePage = lazy(() => import('./modules/management/TimetablePage'));

function LoadingFallback() {
  return (
    <div className="flex items-center justify-center h-screen bg-gray-50">
      <div className="text-center">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading...</p>
      </div>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/login" element={<LoginPage />} />

            {/* Dashboard Layout */}
            <Route path="/" element={<DashboardLayout />}>
              <Route index element={<Navigate to="/dashboard" replace />} />
              <Route path="dashboard" element={<DashboardPage />} />

              {/* Compliance */}
              <Route path="training" element={<TrainingPage />} />
              <Route path="interventions" element={<InterventionsPage />} />
              <Route path="reports" element={<ReportsPage />} />

              {/* Classroom */}
              <Route path="sel-planner" element={<SELPlannerPage />} />
              <Route path="holistic-evaluation" element={<HolisticEvaluationPage />} />
              <Route path="referrals" element={<ReferralsPage />} />

              {/* Parents & Community */}
              <Route path="events" element={<EventsPage />} />
              <Route path="counselling" element={<CounsellingPage />} />

              {/* Students */}
              <Route path="mood-check-in" element={<MoodCheckInPage />} />
              <Route path="career-explorer" element={<CareerExplorerPage />} />
              <Route path="my-counselling" element={<MyCounsellingPage />} />

              {/* Parents */}
              <Route path="child-progress" element={<ChildProgressPage />} />

              {/* Management */}
              <Route path="analytics" element={<AnalyticsPage />} />
              <Route path="users" element={<UsersPage />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="audit-log" element={<AuditLogPage />} />
              <Route path="notifications-manage" element={<NotificationsManagePage />} />
              <Route path="announcements" element={<AnnouncementsPage />} />
              <Route path="attendance" element={<AttendancePage />} />
              <Route path="feedback" element={<FeedbackPage />} />
              <Route path="timetable" element={<TimetablePage />} />

              {/* Students */}
              <Route path="journal" element={<DailyJournalPage />} />
            </Route>

            {/* Fallback */}
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </Router>
    </AuthProvider>
  );
}

export default App;
